import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Building, Layers, Filter, X, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import * as Query from '../../../api/gql/query'
import { PHOTO_TAGS } from '../api'
import { EMPTY_PHOTOS_FILTERS, PhotosFilters, activeFilterCount } from '../lib/filters'
import type { PhotoTag, PhotoVisibility } from '../types'

const ALL = '__all__'
const SEARCH_DEBOUNCE_MS = 400

interface Floor { id: string; name: string; level?: number }
interface BuildingItem { id: string; name: string; code: string | null; floors: Floor[] }

interface Props {
  projectId: string
  filters: PhotosFilters
  onFiltersChange: (filters: PhotosFilters) => void
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</span>
)

const Chip: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <Badge variant="info" className="cursor-pointer gap-1 whitespace-nowrap" onClick={onRemove}>
    {label}
    <X className="h-3 w-3" />
  </Badge>
)

const PhotosFilterBar: React.FC<Props> = ({ projectId, filters, onFiltersChange }) => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [searchText, setSearchText] = useState(filters.search)
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const skip = { variables: { projectId }, skip: !projectId }
  const { data: buildingData } = useQuery(Query.BUILDINGS, skip)
  const { data: settingsData } = useQuery(Query.SNAG_SETTINGS, skip)
  const { data: memberData } = useQuery(Query.PROJECT_MEMBERS, skip)
  const { data: tagData } = useQuery<{ photoTags: PhotoTag[] }>(PHOTO_TAGS, skip)

  const buildings: BuildingItem[] = buildingData?.buildings ?? []
  const trades: string[] = settingsData?.snagSettings?.infrastructureTypes ?? []
  const members: { id: string; name: string }[] = (memberData?.project?.users ?? []).map((u: { user: { id: string; name: string } }) => u.user)
  const tags: PhotoTag[] = tagData?.photoTags ?? []

  useEffect(() => {
    if (searchText === filtersRef.current.search) return undefined
    const timer = setTimeout(() => onFiltersChange({ ...filtersRef.current, search: searchText }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchText]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<PhotosFilters>) => onFiltersChange({ ...filters, ...patch })

  const floors: (Floor & { buildingName?: string })[] = filters.buildingId
    ? buildings.find((b) => b.id === filters.buildingId)?.floors ?? []
    : buildings.reduce<(Floor & { buildingName?: string })[]>((acc, b) => acc.concat((b.floors ?? []).map((f) => ({ ...f, buildingName: b.name }))), [])

  const count = activeFilterCount(filters)
  const toggleTag = (id: string) =>
    set({ tagIds: filters.tagIds.indexOf(id) >= 0 ? filters.tagIds.filter((x) => x !== id) : filters.tagIds.concat(id) })
  const clearAll = () => {
    setSearchText('')
    onFiltersChange({ ...EMPTY_PHOTOS_FILTERS, buildingId: filters.buildingId, floorId: filters.floorId })
  }

  const visibilityLabel = (v: PhotoVisibility) => t(v === 'INTERNAL' ? 'Photos_visibility_internal' : 'Photos_visibility_project')
  const uploader = members.find((m) => m.id === filters.uploadedById)

  return (
    <div className="flex flex-wrap items-center gap-2">
      {buildings.length > 0 && (
        <Select value={filters.buildingId || ALL} onValueChange={(v) => set({ buildingId: v === ALL ? null : v, floorId: null })}>
          <SelectTrigger className="h-8 w-auto min-w-[150px] gap-2 text-sm">
            <Building className="h-4 w-4 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('Photos_all_buildings')}</SelectItem>
            {buildings.map((b) => (
              <SelectItem key={b.id} value={b.id}>{b.code ? `${b.code} - ${b.name}` : b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {floors.length > 0 && (
        <Select value={filters.floorId || ALL} onValueChange={(v) => set({ floorId: v === ALL ? null : v })}>
          <SelectTrigger className="h-8 w-auto min-w-[160px] gap-2 text-sm">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('Photos_all_floors')}</SelectItem>
            {floors.map((f) => (
              <SelectItem key={f.id} value={f.id}>{f.buildingName ? `${f.buildingName} - ${f.name}` : f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Separator orientation="vertical" className="h-5" />

      <Button variant="outline" size="sm" className="relative h-8" onClick={() => setDialogOpen(true)} aria-label={t('Photos_filter_title')}>
        <Filter className="h-4 w-4" />
        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
            {count}
          </span>
        )}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {t('Photos_filter_title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            <div className="space-y-2">
              <SectionTitle>{t('Photos_filter_date')}</SectionTitle>
              <div className="flex items-center gap-2">
                <Input type="date" value={filters.capturedFrom ?? ''} max={filters.capturedTo ?? undefined} aria-label={t('Photos_filter_date_from')} className="h-9" onChange={(e) => set({ capturedFrom: e.target.value || null })} />
                <span className="text-muted-foreground">–</span>
                <Input type="date" value={filters.capturedTo ?? ''} min={filters.capturedFrom ?? undefined} aria-label={t('Photos_filter_date_to')} className="h-9" onChange={(e) => set({ capturedTo: e.target.value || null })} />
              </div>
            </div>

            {trades.length > 0 && (
              <div className="space-y-2">
                <SectionTitle>{t('Photos_filter_trade')}</SectionTitle>
                <Select value={filters.trade || ALL} onValueChange={(v) => set({ trade: v === ALL ? null : v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>{t('Common_all')}</SelectItem>
                    {trades.map((tr) => <SelectItem key={tr} value={tr}>{tr}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {tags.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <SectionTitle>{t('Photos_filter_tags')}</SectionTitle>
                  <div className="flex items-center gap-2">
                    <Switch id="photos-tag-mode" checked={filters.tagMode === 'ALL'} onCheckedChange={(on) => set({ tagMode: on ? 'ALL' : 'ANY' })} />
                    <Label htmlFor="photos-tag-mode" className="text-xs">{t('Photos_filter_tags_all')}</Label>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant={filters.tagIds.indexOf(tag.id) >= 0 ? 'info' : 'secondary'}
                      className="cursor-pointer whitespace-nowrap transition-colors"
                      onClick={() => toggleTag(tag.id)}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <SectionTitle>{t('Photos_filter_uploader')}</SectionTitle>
              <Select value={filters.uploadedById || ALL} onValueChange={(v) => set({ uploadedById: v === ALL ? null : v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('Common_all')}</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <SectionTitle>{t('Photos_filter_visibility')}</SectionTitle>
              <Select value={filters.visibility || ALL} onValueChange={(v) => set({ visibility: v === ALL ? null : (v as PhotoVisibility) })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('Common_all')}</SelectItem>
                  <SelectItem value="PROJECT">{visibilityLabel('PROJECT')}</SelectItem>
                  <SelectItem value="INTERNAL">{visibilityLabel('INTERNAL')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <SectionTitle>{t('Photos_filter_used_in')}</SectionTitle>
              <Select value={filters.usedIn || ALL} onValueChange={(v) => set({ usedIn: v === ALL ? null : (v as 'SNAG' | 'MEETING') })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('Common_all')}</SelectItem>
                  <SelectItem value="SNAG">{t('Photos_used_in_snag')}</SelectItem>
                  <SelectItem value="MEETING">{t('Photos_used_in_meeting')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              {count > 0 ? (
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={clearAll}>
                  <X className="h-3 w-3" />
                  {t('Photos_clear_filters')}
                </Button>
              ) : (
                <div />
              )}
              <Button size="sm" onClick={() => setDialogOpen(false)}>{t('Common_apply', 'Apply')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {(filters.capturedFrom || filters.capturedTo) && (
        <Chip
          label={`${filters.capturedFrom ?? '…'} – ${filters.capturedTo ?? '…'}`}
          onRemove={() => set({ capturedFrom: null, capturedTo: null })}
        />
      )}
      {filters.trade && <Chip label={filters.trade} onRemove={() => set({ trade: null })} />}
      {filters.tagIds.map((id) => {
        const tag = tags.find((x) => x.id === id)
        return tag ? <Chip key={id} label={tag.name} onRemove={() => toggleTag(id)} /> : null
      })}
      {uploader && <Chip label={uploader.name} onRemove={() => set({ uploadedById: null })} />}
      {filters.visibility && <Chip label={visibilityLabel(filters.visibility)} onRemove={() => set({ visibility: null })} />}
      {filters.usedIn && (
        <Chip label={`${t('Photos_filter_used_in')}: ${t(filters.usedIn === 'SNAG' ? 'Photos_used_in_snag' : 'Photos_used_in_meeting')}`} onRemove={() => set({ usedIn: null })} />
      )}

      <div className="flex-grow" />

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t('Photos_search_placeholder')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="h-8 w-[180px] pl-8 text-sm"
        />
        {searchText && (
          <button
            type="button"
            aria-label={t('Common_clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => { setSearchText(''); onFiltersChange({ ...filters, search: '' }) }}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  )
}

export default PhotosFilterBar
